import React, { useMemo } from 'react';
import { Box, useIsCardMailWithin7Days } from '@common-fe/common-fe';
import { FiservCardInterface } from '@common-fe/common-fe';

import { CloseActionCardType } from '@/modules/transaction/Cards/Cards.types';
import { useShouldShowCardActions } from '@/modules/transaction/Cards/hooks/useShouldShowCardActions.tsx';

import CardAccountsAccessCard from './CardStatusActionWrappers/CardAccountsAccessCard';
import CardCloseActionCard from './CardStatusActionWrappers/CardCloseActionCard';
import CardLockUnlockActionCard from './CardStatusActionWrappers/CardLockUnlockActionCard';
import CardReplaceActionCard from './CardStatusActionWrappers/CardReplaceActionCard';

interface CardActionButtonsProps {
  card: FiservCardInterface;
  loadingButton: boolean;
  onOpenLockUnlockCardModal: () => void;
  onOpenCardReplacementModal: () => void;
  onOpenCloseConfirmationModal: () => void;
  onOpenManageAccountAccessModal: () => void;
}

const CardActionButtons: React.FC<CardActionButtonsProps> = ({
  card,
  loadingButton,
  onOpenLockUnlockCardModal,
  onOpenCardReplacementModal,
  onOpenCloseConfirmationModal,
  onOpenManageAccountAccessModal,
}) => {
  const isMailWithin7Days = useIsCardMailWithin7Days(card);
  const { showLockUnlockActionCard, showCloseActionCard, showReplaceActionCard } =
    useShouldShowCardActions(card);
  const showAccountsAccessCard = showLockUnlockActionCard;

  const typeOfCloseActionCard = useMemo(() => {
    if (showLockUnlockActionCard && showReplaceActionCard) {
      return CloseActionCardType.SECONDARY;
    } else {
      return CloseActionCardType.PRIMARY;
    }
  }, [showLockUnlockActionCard, showReplaceActionCard]);

  const singleActionCardVisible =
    [
      showLockUnlockActionCard,
      showReplaceActionCard,
      showCloseActionCard && !(showLockUnlockActionCard && showReplaceActionCard),
    ].filter(Boolean).length === 1;

  const renderActionCards = (
    <Box direction="row" gap="l" justify={singleActionCardVisible ? 'center' : 'start'}>
      {showLockUnlockActionCard && (
        <CardLockUnlockActionCard
          card={card}
          loadingButton={loadingButton}
          onOpenLockUnlockCardModal={onOpenLockUnlockCardModal}
        />
      )}
      {showReplaceActionCard && (
        <CardReplaceActionCard
          card={card}
          isMailWithin7Days={isMailWithin7Days}
          loadingButton={loadingButton}
          onOpenCardReplacementModal={onOpenCardReplacementModal}
        />
      )}
      {showCloseActionCard && !(showLockUnlockActionCard && showReplaceActionCard) && (
        <CardCloseActionCard
          displayType={typeOfCloseActionCard}
          loadingButton={loadingButton}
          onOpenCloseConfirmationModal={onOpenCloseConfirmationModal}
        />
      )}
    </Box>
  );

  return (
    <Box direction="column" pad={{ horizontal: 'l', bottom: 'l' }}>
      {renderActionCards}
      {showAccountsAccessCard && (
        <Box margin={{ top: 'medium' }}>
          <CardAccountsAccessCard
            loadingButton={loadingButton}
            onOpenManageAccountAccessModal={onOpenManageAccountAccessModal}
          />
        </Box>
      )}
      {showCloseActionCard && showLockUnlockActionCard && showReplaceActionCard && (
        <Box margin={{ top: 'medium' }}>
          <CardCloseActionCard
            displayType={typeOfCloseActionCard}
            loadingButton={loadingButton}
            onOpenCloseConfirmationModal={onOpenCloseConfirmationModal}
          />
        </Box>
      )}
    </Box>
  );
};

export default React.memo(CardActionButtons);
